import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Layout } from "@/components/Layout";
import {
  Building2,
  Plus,
  MapPin,
  Radio,
  Clock,
  Trash2,
  Edit,
  Play,
  Save,
  AlertTriangle,
  X,
  ChevronRight,
  Download,
  Upload,
  FileJson,
  Copy,
  Check,
  Calendar,
  History,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

export default function VenuesPage({ user, onLogout }) {
  const { t } = useTranslation();
  const [venues, setVenues] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEventHistory, setShowEventHistory] = useState(false);
  const [venueEvents, setVenueEvents] = useState([]);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [maxVenues, setMaxVenues] = useState(3);
  const [currentPlan, setCurrentPlan] = useState("free");

  const [form, setForm] = useState({
    name: "",
    city: "",
    region: "",
    address: "",
    equipment_id: "",
    notes: "",
    frequencies: [],
  });

  // Import/Export states
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importData, setImportData] = useState(null);
  const [importError, setImportError] = useState("");
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    fetchVenues();
    fetchEquipment();
    fetchRegions();
  }, []);

  const fetchVenues = async () => {
    try {
      const response = await axios.get(`${API_BASE}/venues`, getAuthHeaders());
      setVenues(response.data.venues);
      setMaxVenues(response.data.max_venues);
      setCurrentPlan(response.data.plan);
    } catch (error) {
      // Error handled silently
      toast.error(t("errors.generic"));
    } finally {
      setLoading(false);
    }
  };

  const fetchEquipment = async () => {
    try {
      const response = await axios.get(`${API_BASE}/equipment`, getAuthHeaders());
      setEquipment(response.data.equipment);
    } catch (error) {
      // Error handled silently
    }
  };

  const fetchRegions = async () => {
    try {
      const response = await axios.get(`${API_BASE}/frequencies/chile/regions`, getAuthHeaders());
      setRegions(response.data.regions);
    } catch (error) {
      // Error handled silently
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.city) {
      toast.error("Nombre y ciudad son requeridos");
      return;
    }

    try {
      if (selectedVenue) {
        // Update existing venue
        await axios.put(`${API_BASE}/venues/${selectedVenue.id}`, form, getAuthHeaders());
        toast.success("Venue actualizado exitosamente");
      } else {
        // Create new venue
        await axios.post(`${API_BASE}/venues`, form, getAuthHeaders());
        toast.success("Venue creado exitosamente");
      }
      setShowModal(false);
      resetForm();
      fetchVenues();
    } catch (error) {
      // Error handled silently
      toast.error(error.response?.data?.detail || t("errors.generic"));
    }
  };

  const handleEdit = (venue) => {
    setSelectedVenue(venue);
    setForm({
      name: venue.name,
      city: venue.city,
      region: venue.region || "",
      address: venue.address || "",
      equipment_id: venue.equipment_id || "",
      notes: venue.notes || "",
      frequencies: venue.frequencies || [],
    });
    setShowModal(true);
  };

  const handleDelete = async () => {
    if (!selectedVenue) return;
    try {
      await axios.delete(`${API_BASE}/venues/${selectedVenue.id}`, getAuthHeaders());
      toast.success("Venue eliminado exitosamente");
      setShowDeleteDialog(false);
      setSelectedVenue(null);
      fetchVenues();
    } catch (error) {
      // Error handled silently
      toast.error(t("errors.generic"));
    }
  };

  const handleLoadConfig = async (venue) => {
    try {
      const response = await axios.post(
        `${API_BASE}/venues/${venue.id}/load`,
        {},
        getAuthHeaders()
      );
      toast.success(`Configuración de ${venue.name} cargada`);
      // Here you could navigate to systems page or frequencies page with the loaded config
      fetchVenues(); // Refresh to update last_used
    } catch (error) {
      // Error handled silently
      toast.error(t("errors.generic"));
    }
  };

  const handleShowEventHistory = async (venue) => {
    setSelectedVenue(venue);
    try {
      const response = await axios.get(
        `${API_BASE}/events/by-venue/${venue.id}`,
        getAuthHeaders()
      );
      setVenueEvents(response.data.events || []);
      setShowEventHistory(true);
    } catch (error) {
      // Error handled silently
      setVenueEvents([]);
      setShowEventHistory(true);
    }
  };

  const resetForm = () => {
    setSelectedVenue(null);
    setForm({
      name: "",
      city: "",
      region: "",
      address: "",
      equipment_id: "",
      notes: "",
      frequencies: [],
    });
  };

  const openNewVenueModal = () => {
    resetForm();
    setShowModal(true);
  };

  const getEquipmentName = (equipmentId) => {
    const eq = equipment.find((e) => e.id === equipmentId);
    return eq ? eq.full_name : equipmentId;
  };

  const formatDate = (dateString) => {
    if (!dateString) return t("venues.card.never");
    return new Date(dateString).toLocaleDateString("es-CL", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const canAddVenue = venues.length < maxVenues;

  // Export venue as JSON
  const handleExportVenue = (venue) => {
    const exportData = {
      version: "1.0",
      exported_at: new Date().toISOString(),
      platform: "AVX RF PRO",
      venue: {
        name: venue.name,
        city: venue.city,
        region: venue.region || "",
        address: venue.address || "",
        equipment_id: venue.equipment_id || "",
        notes: venue.notes || "",
        frequencies: venue.frequencies || [],
      },
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `venue_${venue.name.replace(/\s+/g, "_").toLowerCase()}_${new Date().toISOString().split("T")[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success(`Venue "${venue.name}" exportado correctamente`);
  };

  // Export all venues
  const handleExportAllVenues = () => {
    if (venues.length === 0) {
      toast.error("No hay venues para exportar");
      return;
    }

    const exportData = {
      version: "1.0",
      exported_at: new Date().toISOString(),
      platform: "AVX RF PRO",
      venues: venues.map((venue) => ({
        name: venue.name,
        city: venue.city,
        region: venue.region || "",
        address: venue.address || "",
        equipment_id: venue.equipment_id || "",
        notes: venue.notes || "",
        frequencies: venue.frequencies || [],
      })),
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `avx_rf_pro_venues_${new Date().toISOString().split("T")[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success(`${venues.length} venues exportados correctamente`);
  };

  // Handle file selection for import
  const handleImportFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImportError("");
    setImportData(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);
        
        // Validate structure
        if (!data.platform || data.platform !== "AVX RF PRO") {
          setImportError("Archivo no válido. Debe ser exportado desde AVX RF PRO.");
          return;
        }

        // Check if it's single venue or multiple
        if (data.venue) {
          setImportData({ type: "single", venues: [data.venue] });
        } else if (data.venues && Array.isArray(data.venues)) {
          setImportData({ type: "multiple", venues: data.venues });
        } else {
          setImportError("Estructura de archivo no reconocida.");
          return;
        }

        setShowImportDialog(true);
      } catch (err) {
        setImportError("Error al leer el archivo JSON. Verifica el formato.");
      }
    };
    reader.readAsText(file);
    
    // Reset input
    e.target.value = "";
  };

  // Confirm import
  const handleConfirmImport = async () => {
    if (!importData || importData.venues.length === 0) return;

    const availableSlots = maxVenues - venues.length;
    const venuesToImport = importData.venues.slice(0, availableSlots);

    if (venuesToImport.length === 0) {
      toast.error("No tienes espacio para importar venues. Mejora tu plan.");
      return;
    }

    setImporting(true);
    let successCount = 0;

    for (const venueData of venuesToImport) {
      try {
        await axios.post(`${API_BASE}/venues`, venueData, getAuthHeaders());
        successCount++;
      } catch (error) {
        // Error handled silently
      }
    }

    setImporting(false);
    setShowImportDialog(false);
    setImportData(null);

    if (successCount > 0) {
      toast.success(`${successCount} venue(s) importado(s) correctamente`);
      fetchVenues();
    } else {
      toast.error("Error al importar venues");
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="top-bar">
        <div>
          <h1 className="font-heading text-xl font-bold text-[#F4F4F5]">
            {t("venues.title")}
          </h1>
          <p className="text-sm text-[#71717A]">{t("venues.subtitle")}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm text-[#71717A]">
            <span className="font-mono text-[#34C759]">{venues.length}</span>
            <span> / </span>
            <span className="font-mono">{maxVenues === 999 ? "∞" : maxVenues}</span>
            <span className="ml-1">{t("venues.title").toLowerCase()}</span>
          </div>
          
          {/* Import/Export buttons */}
          <div className="flex items-center gap-2">
            <input
              type="file"
              id="import-venue-file"
              accept=".json"
              onChange={handleImportFileSelect}
              className="hidden"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => document.getElementById("import-venue-file").click()}
              className="border-white/20 text-[#A1A1AA] hover:text-white hover:border-white/40"
              data-testid="import-venues-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Importar
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportAllVenues}
              disabled={venues.length === 0}
              className="border-white/20 text-[#A1A1AA] hover:text-white hover:border-white/40"
              data-testid="export-all-venues-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Exportar Todo
            </Button>
          </div>
          
          <Button
            onClick={openNewVenueModal}
            disabled={!canAddVenue}
            className="btn-tactical btn-tactical-primary"
            data-testid="add-venue-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t("venues.addVenue")}
          </Button>
        </div>
      </div>

      {/* Limit Warning */}
      {!canAddVenue && (
        <div className="mb-6 p-4 bg-[#FFCC00]/10 border border-[#FFCC00]/30 rounded-sm flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-[#FFCC00] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-[#FFCC00] font-medium">{t("venues.limits.reached")}</p>
            <p className="text-sm text-[#A1A1AA] mt-1">{t("venues.limits.upgrade")}</p>
          </div>
        </div>
      )}

      {/* Venues Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <p className="text-[#71717A]">{t("common.loading")}</p>
        </div>
      ) : venues.length === 0 ? (
        <div className="bg-[#111111] border border-white/[0.08] rounded-sm p-12 text-center">
          <Building2 className="w-16 h-16 mx-auto mb-4 text-[#71717A] opacity-30" />
          <h3 className="font-heading text-lg text-[#F4F4F5] mb-2">
            {t("venues.noVenues")}
          </h3>
          <p className="text-[#71717A] mb-6">{t("venues.noVenuesDesc")}</p>
          <Button
            onClick={openNewVenueModal}
            className="btn-tactical btn-tactical-primary"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t("venues.addVenue")}
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {venues.map((venue) => (
            <div
              key={venue.id}
              className="bg-[#111111] border border-white/[0.08] rounded-sm p-5 hover:border-[#34C759]/30 transition-colors group"
              data-testid={`venue-card-${venue.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-heading font-semibold text-[#F4F4F5] group-hover:text-[#34C759] transition-colors">
                    {venue.name}
                  </h3>
                  <div className="flex items-center gap-1 text-sm text-[#71717A] mt-1">
                    <MapPin className="w-3 h-3" />
                    <span>{venue.city}</span>
                    {venue.region && (
                      <>
                        <span className="mx-1">•</span>
                        <span>{venue.region}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-[#71717A] hover:text-[#F4F4F5]"
                    onClick={() => handleEdit(venue)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-[#71717A] hover:text-[#FF3B30]"
                    onClick={() => {
                      setSelectedVenue(venue);
                      setShowDeleteDialog(true);
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2 text-sm mb-4">
                {venue.equipment_id && (
                  <div className="flex items-center gap-2">
                    <Radio className="w-4 h-4 text-[#34C759]" />
                    <span className="text-[#A1A1AA]">
                      {getEquipmentName(venue.equipment_id)}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <span className="text-[#71717A]">{t("venues.card.frequencies")}:</span>
                  <span className="font-mono text-[#34C759]">
                    {venue.frequencies?.length || 0}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#71717A]" />
                  <span className="text-[#71717A]">
                    {t("venues.card.lastUsed")}: {formatDate(venue.last_used)}
                  </span>
                </div>
              </div>

              {venue.notes && (
                <p className="text-xs text-[#71717A] mb-4 line-clamp-2">
                  {venue.notes}
                </p>
              )}

              <div className="flex gap-2 pt-3 border-t border-white/[0.08]">
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex-1 text-[#34C759] hover:text-[#34C759] hover:bg-[#34C759]/10"
                  onClick={() => handleLoadConfig(venue)}
                >
                  <Play className="w-4 h-4 mr-1" />
                  {t("venues.card.loadConfig")}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-[#71717A] hover:text-blue-400 hover:bg-blue-500/10"
                  onClick={() => handleShowEventHistory(venue)}
                  data-testid={`history-venue-${venue.id}`}
                  title="Ver historial de eventos"
                >
                  <History className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-[#71717A] hover:text-[#3B82F6] hover:bg-[#3B82F6]/10"
                  onClick={() => handleExportVenue(venue)}
                  data-testid={`export-venue-${venue.id}`}
                >
                  <Download className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Venue Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-[#111111] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#34C759]" />
              {selectedVenue ? "Editar Venue" : t("venues.addVenue")}
            </DialogTitle>
            <DialogDescription className="text-[#71717A]">
              {t("venues.form.title")}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#A1A1AA]">{t("venues.form.name")} *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="input-tactical"
                  placeholder={t("venues.form.namePlaceholder")}
                  required
                  data-testid="venue-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#A1A1AA]">{t("venues.form.city")} *</Label>
                <Input
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  className="input-tactical"
                  placeholder={t("venues.form.cityPlaceholder")}
                  required
                  data-testid="venue-city-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#A1A1AA]">{t("venues.form.region")}</Label>
                <Select
                  value={form.region}
                  onValueChange={(v) => setForm({ ...form, region: v })}
                >
                  <SelectTrigger className="input-tactical">
                    <SelectValue placeholder="Seleccionar región" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10 max-h-[200px]">
                    {regions.map((region) => (
                      <SelectItem key={region.id} value={region.name}>
                        {region.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-[#A1A1AA]">{t("venues.form.equipment")}</Label>
                <Select
                  value={form.equipment_id}
                  onValueChange={(v) => setForm({ ...form, equipment_id: v })}
                >
                  <SelectTrigger className="input-tactical">
                    <SelectValue placeholder="Seleccionar equipo" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    {equipment.map((eq) => (
                      <SelectItem key={eq.id} value={eq.id}>
                        {eq.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[#A1A1AA]">{t("venues.form.address")}</Label>
              <Input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="input-tactical"
                placeholder={t("venues.form.addressPlaceholder")}
                data-testid="venue-address-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#A1A1AA]">{t("venues.form.notes")}</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="input-tactical h-20 resize-none"
                placeholder={t("venues.form.notesPlaceholder")}
                data-testid="venue-notes-input"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowModal(false)}
                className="text-[#A1A1AA] hover:text-white"
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                className="btn-tactical btn-tactical-primary"
                data-testid="venue-submit-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {selectedVenue ? t("common.save") : t("common.create")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-[#111111] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              ¿Eliminar {selectedVenue?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#71717A]">
              Esta acción no se puede deshacer. Se eliminarán todas las
              configuraciones guardadas para este venue.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/10 text-white hover:bg-white/5">
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-[#FF3B30] hover:bg-[#FF3B30]/80 text-white"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import Confirmation Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="bg-[#111111] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <FileJson className="w-5 h-5 text-[#3B82F6]" />
              Importar Venues
            </DialogTitle>
            <DialogDescription className="text-[#71717A]">
              Revisa los datos antes de importar
            </DialogDescription>
          </DialogHeader>

          {importError ? (
            <div className="p-4 bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-sm">
              <p className="text-[#FF3B30] text-sm">{importError}</p>
            </div>
          ) : importData ? (
            <div className="space-y-4">
              <div className="p-3 bg-[#0A0A0A] border border-white/[0.05] rounded-sm">
                <p className="text-xs text-[#71717A] mb-2">
                  {importData.type === "single" ? "1 venue encontrado" : `${importData.venues.length} venues encontrados`}
                </p>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {importData.venues.map((venue, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-2 bg-[#111111] rounded"
                    >
                      <div>
                        <p className="text-sm font-medium text-white">{venue.name}</p>
                        <p className="text-xs text-[#71717A]">
                          {venue.city} • {venue.frequencies?.length || 0} frecuencias
                        </p>
                      </div>
                      <Check className="w-4 h-4 text-[#34C759]" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Capacity warning */}
              {importData.venues.length > maxVenues - venues.length && (
                <div className="p-3 bg-[#FFCC00]/10 border border-[#FFCC00]/30 rounded-sm flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-[#FFCC00] flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-[#FFCC00]">
                    Solo puedes importar {maxVenues - venues.length} venue(s) más. 
                    Se importarán los primeros {Math.min(importData.venues.length, maxVenues - venues.length)}.
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-3">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowImportDialog(false);
                    setImportData(null);
                  }}
                  className="text-[#A1A1AA] hover:text-white"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleConfirmImport}
                  disabled={importing || maxVenues - venues.length === 0}
                  className="bg-[#3B82F6] hover:bg-[#3B82F6]/90 text-white"
                  data-testid="confirm-import-btn"
                >
                  {importing ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Importando...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Importar {Math.min(importData.venues.length, maxVenues - venues.length)} Venue(s)
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Event History Dialog */}
      <Dialog open={showEventHistory} onOpenChange={setShowEventHistory}>
        <DialogContent className="bg-[#111111] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <History className="w-5 h-5 text-blue-400" />
              Historial de Eventos
            </DialogTitle>
            <DialogDescription className="text-[#71717A]">
              Eventos realizados en {selectedVenue?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-3">
            {venueEvents.length === 0 ? (
              <div className="text-center py-8 bg-[#0A0A0A] rounded-lg border border-white/[0.05]">
                <Calendar className="w-10 h-10 mx-auto mb-3 text-[#71717A] opacity-30" />
                <p className="text-sm text-[#71717A]">No hay eventos registrados en este venue</p>
                <p className="text-xs text-[#52525B] mt-1">
                  Vincula eventos a este venue para ver el historial
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {venueEvents.map(event => (
                  <div
                    key={event.id}
                    className="p-4 bg-[#0A0A0A] rounded-lg border border-white/[0.05]"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-[#F4F4F5]">{event.name}</h4>
                        <p className="text-xs text-[#71717A] flex items-center gap-1 mt-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(event.date).toLocaleDateString('es-CL', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {event.groups?.length > 0 && (
                          <Badge className="bg-green-500/20 text-green-400 text-[10px]">
                            {event.groups.length} grupos
                          </Badge>
                        )}
                        <Badge className="bg-blue-500/20 text-blue-400 text-[10px]">
                          {event.rf_config?.length || 0} sistemas
                        </Badge>
                      </div>
                    </div>
                    {event.notes && (
                      <p className="text-xs text-[#52525B] mt-2 line-clamp-2">{event.notes}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            <div className="pt-4 border-t border-white/[0.08] text-center">
              <p className="text-xs text-[#52525B]">
                Total: {venueEvents.length} evento{venueEvents.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
